import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import { Settings } from '@repo/shared/domain';
import type { AgenticParsingSettings } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { getMultipleSettings, updateSettings } from 'src/api/admin/settings';

export const Route = createFileRoute('/_admin/system/agentic-parsing')({
	component: AgenticParsingPage,
});

type State = Partial<NonNullable<AgenticParsingSettings['settings']>>;

function statusLabel(status: string): string {
	switch (status) {
		case 'applying':
			return 'Enabling, converting document types and tasks…';
		case 'reverting':
			return 'Disabling, restoring classic tasks…';
		case 'failed':
			return 'Failed';
		default:
			return 'Idle';
	}
}

function AgenticParsingPage() {
	const queryClient = useQueryClient();
	const [confirming, setConfirming] = useState<null | boolean>(null);
	const [showEffects, setShowEffects] = useState(false);

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Agentic Parsing');

	const { data, isLoading } = useQuery({
		queryKey: ['agentic-parsing-settings'],
		queryFn: () => getMultipleSettings([Settings.AgenticParsing]),
		// Poll while a conversion runs so the UI reflects applying → idle.
		refetchInterval: (query) => {
			const s = ((query.state.data as Record<string, { settings?: State }> | undefined)?.[Settings.AgenticParsing]
				?.settings ?? {}) as State;
			return s.status === 'applying' || s.status === 'reverting' ? 1500 : false;
		},
	});

	const state = ((data?.[Settings.AgenticParsing] as { settings?: State })?.settings ?? {}) as State;
	const enabled = state.enabled === true;
	const status = state.status ?? 'idle';
	const busy = status === 'applying' || status === 'reverting';
	const counts = state.counts;
	const warnings = state.warnings ?? [];
	const target = !enabled;

	const mutation = useMutation({
		mutationFn: (next: boolean) => updateSettings<{ enabled: boolean }>(Settings.AgenticParsing, { enabled: next }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['agentic-parsing-settings'] });
			EventEmitter.emit('toast', { type: 'success', message: 'Conversion started. This may take a moment.' });
		},
		onError: () => EventEmitter.emit('toast', { type: 'error', message: 'Failed to update the agentic parsing setting.' }),
	});

	if (isLoading) {
		return (
			<div className="flex items-center justify-center p-16">
				<CircularProgress size={28} />
			</div>
		);
	}

	return (
		<div className="min-h-full w-full p-4">
			<Paper variant="outlined" className="p-6">
				<h2 className="text-lg font-semibold">Agentic Document Parsing</h2>
				<p className="mt-1 text-sm text-gray-500">NineID internal switch for the whole tenant.</p>

				<Alert severity="info" className="mt-3 mb-4 max-w-3xl">
					This changes how identity documents (ID card, passport, residence permit) and Belgian social security
					documents are captured from the applicant and read by our parser. Flipping the switch starts a background
					conversion and the status below shows its progress. Please read the effects before switching, because
					applicants who are filling in a task right now can be affected.
				</Alert>

				<div className="mb-4 grid max-w-4xl gap-4 md:grid-cols-2">
					<div>
						<div className="mb-1 text-sm font-semibold">When you turn it on</div>
						<ul className="list-disc pl-5 text-sm text-gray-600">
							<li>
								The parser switches to the agentic AI parser for every ID card, passport, residence permit and
								Belgian social security upload in this tenant.
							</li>
							<li>
								Simple identification tasks give the applicant a guided capture that walks through the document
								step by step, instead of a plain file upload. Tasks that accepted any ID now ask for an ID card or
								passport. The task type and your configuration stay the same.
							</li>
							<li>
								Custom tasks with an ID Card section switch to the guided agentic capture. The driving licence
								option is removed, because the AI parser cannot read driving licences. Two upload options, name
								validation and continue on error, no longer apply while it is on.
							</li>
							<li>
								The applicant sees live verification feedback and has to acknowledge any review flags before
								continuing.
							</li>
							<li>
								Some flows stay the same. Tasks that collect an ID through a plain document upload section keep the
								classic upload screen, and the AI still reads the file even though the screen looks the same. Social
								security section tasks look the same, and company and approval tasks are not converted.
							</li>
						</ul>
					</div>
					<div>
						<div className="mb-1 text-sm font-semibold">When you turn it off</div>
						<ul className="list-disc pl-5 text-sm text-gray-600">
							<li>
								Everything goes back to how it was before you turned it on. Document types return to their previous
								parser, each type to its own earlier setting.
							</li>
							<li>
								Simple tasks return to the classic upload. Custom ID sections are restored exactly, so the driving
								licence option, the upload options and the camera zoom all come back. Tasks that were asking for an
								ID card or passport go back to accepting any ID.
							</li>
							<li>The restore uses a snapshot taken the moment you turned it on, so in normal use it is exact.</li>
							<li>Applicants who are in the middle of a task may again see their screen change back.</li>
						</ul>
					</div>
				</div>

				<button
					type="button"
					onClick={() => setShowEffects((v) => !v)}
					className="mb-2 text-sm font-medium text-gray-700 underline"
				>
					{showEffects ? 'Hide side effects and cautions' : 'Show side effects and cautions'}
				</button>
				{showEffects && (
					<Alert severity="warning" className="mb-4 max-w-4xl">
						<ul className="list-disc pl-5 text-sm">
							<li>
								It is not instant. The switch starts a background job and the status shows Enabling or Disabling
								while it converts every affected task and document type. Do not flip it again until it returns to
								Idle, because a second flip while it is running does nothing.
							</li>
							<li>
								Applicants can be disrupted. The change applies straight away, so someone in the middle of an
								identity task may see the screen switch between the classic upload and the guided capture. Tasks
								that are already submitted are not affected and their data stays exactly as captured.
							</li>
							<li>
								Do not edit identification templates while the job is running, because a change at the same time can
								conflict with the conversion.
							</li>
							<li>
								Tasks whose translations are still being generated are skipped and have to be run again after the
								translation finishes. They appear in the warnings.
							</li>
							<li>
								Standard wording is applied again to the simple task screens. The terms text and links you configured
								are kept, but any other wording you edited by hand resets to the standard text.
							</li>
						</ul>
					</Alert>
				)}

				<div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
					<div>
						<div className="text-sm font-medium">Agentic parsing is {enabled ? 'ON' : 'OFF'}</div>
						<div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
							{busy && <CircularProgress size={12} />}
							{statusLabel(status)}
						</div>
					</div>
					<Button
						variant="contained"
						color={target ? 'primary' : 'error'}
						disabled={busy || mutation.isPending}
						onClick={() => setConfirming(target)}
					>
						{target ? 'Enable agentic parsing' : 'Disable agentic parsing'}
					</Button>
				</div>

				{status === 'failed' && state.lastError && (
					<Alert severity="error" className="mt-4">
						<div>{state.lastError}</div>
						<div className="mt-1 text-xs">
							The last conversion failed and was rolled back to the classic setup. Check the error above and try
							again once the status shows Idle. If it stays on Failed it needs manual attention.
						</div>
					</Alert>
				)}

				{enabled && counts && (
					<p className="mt-4 text-xs text-gray-500">
						Converted {counts.templates ?? 0} task template(s) ({counts.tasks ?? 0} task instance(s)) and flipped{' '}
						{counts.docTypes ?? 0} document type(s).
					</p>
				)}

				{warnings.length > 0 && (
					<div className="mt-4 flex flex-col gap-2">
						{warnings.map((w) => (
							<Alert key={w} severity="warning">
								{w}
							</Alert>
						))}
					</div>
				)}
			</Paper>

			<Dialog open={confirming !== null} onClose={() => setConfirming(null)}>
				<DialogTitle>
					{confirming ? 'Enable agentic parsing?' : 'Disable agentic parsing?'}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{confirming
							? 'This starts a background conversion. It switches ID card, passport, residence permit and Belgian social security documents to the AI parser, replaces the plain ID upload with guided capture on simple and custom ID tasks, and removes the driving licence option where it was offered. You can turn it back off at any time to restore everything.'
							: 'This restores the classic parser and the classic upload screens for every identification task, exactly as they were before it was enabled, including the driving licence option and any upload settings.'}
					</DialogContentText>
					<Alert severity="warning" sx={{ mt: 2 }}>
						Applicants filling in an identity task right now may see their screen change{confirming ? '' : ' back'}.
					</Alert>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirming(null)}>Cancel</Button>
					<Button
						variant="contained"
						color={confirming ? 'primary' : 'error'}
						onClick={() => {
							if (confirming !== null) mutation.mutate(confirming);
							setConfirming(null);
						}}
					>
						{confirming ? 'Enable' : 'Disable'}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
